import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import Product from '../../../../model/product/product';
import { SelectorComponent } from '../selector/selector.component';
import { SubscriptionBasedComponent } from '../../../../subscription-helpers/subscription-based.component';
import RequestedProductSize from 'src/app/model/product/requested-product-size';
import ProductHelper from 'src/app/helpers/product-helper';
import ProductSize from 'src/app/model/product/product-size';
import Windowsticker from 'src/app/model/product/windowsticker/windowsticker';
import WindowStickerSize from 'src/app/model/product/windowsticker/windowsticker-size';
import StandardStickerPart from 'src/app/model/product/windowsticker/standard-sticker-part';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';

@Component({
  selector: 'size-picker',
  templateUrl: './size-picker.component.html',
  styleUrls: ['./size-picker.component.scss'],
})
export class SizePickerComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  width: number;
  height: number;
  position: string;

  widthOptions: Array<number>;
  heightOptions: Array<number>;

  @Input()
  public inputProduct: Product;

  @Input()
  public inputStandardStickerPart: StandardStickerPart;

  @Input()
  public index: number;

  @Input()
  public productSizes: ProductSize[];

  @Input()
  productMaterialChanged: EventEmitter<any>;

  @Input()
  client = false;

  @Input()
  disabled = false;

  @Output()
  public updateProduct = new EventEmitter();

  @ViewChild('widthSelector', { static: true })
  widthComponent: SelectorComponent;

  @ViewChild('heightSelector', { static: true })
  heightComponent: SelectorComponent;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit() {
    this.widthOptions = [];
    this.heightOptions = [];

    this.setHeightOptions();
    this.setWidthOptions();

    if (this.inputStandardStickerPart.requestedProductSize) {
      let width = this.inputStandardStickerPart.requestedProductSize.width;
      let height = this.inputStandardStickerPart.requestedProductSize.height;

      if (width === height) {
        this.positionChange('square');
      } else if (width > height) {
        this.positionChange('horizontal');
      } else {
        this.positionChange('vertical');
      }

      this.width = width;
      this.height = height;
    }

    this.addSubscription(this.productMaterialChanged, () => {
      this.setHeightOptions();
      this.setWidthOptions();
      this.width = null;
      this.height = null;
      delete this.inputStandardStickerPart.requestedProductSize;
      delete this.inputStandardStickerPart.productSize;
    });
  }

  setWidth(event) {
    this.width = event;
    if (this.client) {
      delete this.inputStandardStickerPart.productSize;
    }
    this.projectDisplayObservableService
      .getProductMaterialChangedSubscribable()
      .next();
    this.projectDisplayObservableService
      .getProjectInvoicePriceChangedSubscribable()
      .next();
    this.emitData();
  }

  setHeight(event) {
    this.height = event;
    this.width = null;
    this.setWidthOptions(event);
    if (this.client) {
      delete this.inputStandardStickerPart.productSize;
    }
    this.projectDisplayObservableService
      .getProductMaterialChangedSubscribable()
      .next();
    this.projectDisplayObservableService
      .getProjectInvoicePriceChangedSubscribable()
      .next();
    this.emitData();
  }

  positionChange(event) {
    this.position = event;
    this.width = null;
    this.height = null;
    if (this.client) {
      delete this.inputStandardStickerPart.productSize;
    }
    this.setHeightOptions();
    this.setWidthOptions();
    this.projectDisplayObservableService
      .getProductMaterialChangedSubscribable()
      .next();
    this.projectDisplayObservableService
      .getProjectInvoicePriceChangedSubscribable()
      .next();
  }

  squareFilter() {
    let array = [];
    for (let ps of this.productSizes) {
      if (
        ps.height === ps.width &&
        ProductHelper.productSizeAllowedForProduct(this.inputProduct, ps)
      ) {
        array.push(ps);
      }
    }
    return array;
  }

  verticalFilter() {
    let array = [];
    for (let ps of this.productSizes) {
      if (
        ps.height > ps.width &&
        ProductHelper.productSizeAllowedForProduct(this.inputProduct, ps)
      ) {
        array.push(ps);
      }
    }
    return array;
  }

  horizontalFilter() {
    let array = [];
    for (let ps of this.productSizes) {
      if (
        ps.height < ps.width &&
        ProductHelper.productSizeAllowedForProduct(this.inputProduct, ps)
      ) {
        array.push(ps);
      }
    }
    return array;
  }

  getFilteredOptions() {
    switch (this.position) {
      case 'square': {
        return this.squareFilter();
      }
      case 'horizontal': {
        return this.horizontalFilter();
      }
      case 'vertical': {
        return this.verticalFilter();
      }
      default: {
        return [];
      }
    }
  }

  emitData() {
    if (this.width && this.height) {
      let chosenSize = this.productSizes.filter((productSize) => {
        return (
          productSize.width === this.width && productSize.height === this.height
        );
      });
      let newRequestedProductSize = new RequestedProductSize();
      newRequestedProductSize.height = chosenSize[0].height;
      newRequestedProductSize.width = chosenSize[0].width;
      this.inputStandardStickerPart.requestedProductSize =
        newRequestedProductSize;
      let matchingProductSize = chosenSize.find((s) =>
        this.matchesChosenMaterial(s)
      );
      if (matchingProductSize != null) {
        this.inputStandardStickerPart.productSize = matchingProductSize;
      }
      this.setDesignerTimeBudgets();
      this.setInstallerTimeBudgets();
      this.updateProduct.emit();
    }
  }

  setWidthOptions(height?) {
    if (this.heightOptions.length > 0) {
      if (height !== undefined) {
        this.widthOptions = [];
        for (let ps of this.getFilteredOptions()) {
          if (ps.height === height) {
            if (!this.widthOptions.includes(ps.width)) {
              this.widthOptions.push(ps.width);
            }
          }
        }
      } else {
        for (let ps of this.getFilteredOptions()) {
          if (!this.widthOptions.includes(ps.width)) {
            this.widthOptions.push(ps.width);
          }
        }
      }
    } else {
      this.widthOptions = [];
    }
  }

  setHeightOptions() {
    this.heightOptions = [];
    for (let ps of this.getFilteredOptions()) {
      if (!this.heightOptions.includes(ps.height)) {
        this.heightOptions.push(ps.height);
      }
    }
  }

  matchesChosenMaterial(productSize: ProductSize) {
    if (
      this.inputProduct.type === 'WindowSticker' ||
      this.inputProduct.type === 'CustomWindowSticker'
    ) {
      let windowSticker = <Windowsticker>this.inputProduct;
      let windowStickerSize = <WindowStickerSize>productSize;
      return (
        windowSticker != null &&
        windowStickerSize != null &&
        windowSticker.foil != null &&
        windowStickerSize.material != null &&
        windowSticker.foil.ordinal === windowStickerSize.material.ordinal
      );
    }
    return false;
  }

  private setDesignerTimeBudgets(): void {
    const hoursAndMinutes =
      ProductHelper.getStandardWindowStickerLongestTimeBudget(
        <Windowsticker>this.inputProduct,
        'designerMinutes'
      );
    this.inputProduct.designerHourBudget =
      hoursAndMinutes != null ? hoursAndMinutes[0] : null;
    this.inputProduct.designerMinuteBudget =
      hoursAndMinutes != null ? hoursAndMinutes[1] : null;
  }

  private setInstallerTimeBudgets(): void {
    if (this.inputProduct.productPlacement != null) {
      const hoursAndMinutes =
        ProductHelper.getStandardWindowStickerTimeBudgetSum(
          <Windowsticker>this.inputProduct,
          'placerMinutes'
        );
      this.inputProduct.productPlacement.installerHourBudget =
        hoursAndMinutes != null ? hoursAndMinutes[0] : null;
      this.inputProduct.productPlacement.installerMinuteBudget =
        hoursAndMinutes != null ? hoursAndMinutes[1] : null;
    }
  }
}
